import {Capability} from "crqlar-react-common/dist/models/app/app.model";
import {AppSku} from "crqlar-react-common/dist/models/app/appSku";

export type SettingsPermission = Capability

// Sidebar/search/access metadata only. App.tsx owns the actual React routes.
export type SettingsNavigationDefinition = {
    id: string
    sectionId: string
    sectionLabel: string
    label: string
    path: string
    description?: string
    createLabel?: string
    createPath?: string
    keywords?: readonly string[]
    requiredPermissions: readonly SettingsPermission[]
    requiresGuestAppModule?: boolean
    requiresCanViewLogs?: boolean
    requiresAdmin?: boolean
    /** Shown only when the organisation has the app with this SKU and the user may access it. */
    requiredAppSku?: string
}

export type SettingsItem = {
    id: string
    label: string
    path: string
    description?: string
    createLabel?: string
    createPath?: string
    keywords?: readonly string[]
}

export type SettingsSection = {
    id: string
    label: string
    items: SettingsItem[]
}

const normalizePathname = (pathname: string) =>
    pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname

const isSameOrChildPath = (pathname: string, routePath: string) =>
    pathname === routePath || pathname.startsWith(`${routePath}/`)

export const SETTINGS_NAVIGATION_DEFINITIONS = [
    {
        id: 'profiles',
        sectionId: 'guests',
        sectionLabel: 'Guests',
        label: 'Profiles',
        path: '/system/profiles',
        description: 'Overview of all guest profiles in the system. Add or edit all profile details easily.',
        createLabel: 'Create profile',
        createPath: '/system/profiles/new',
        keywords: ['guests', 'customers'],
        requiredPermissions: [Capability.GET_PROFILES],
    },
    {
        id: 'guestAppUsers',
        sectionId: 'guests',
        sectionLabel: 'Guests',
        label: 'App users',
        path: '/system/guestAppUsers',
        description: 'Overview of all app accounts.',
        createLabel: 'Create guest app user',
        createPath: '/system/guestAppUsers/new',
        requiredPermissions: [Capability.GET_PROFILES],
        requiresGuestAppModule: true,
    },
    {
        id: 'deduplication',
        sectionId: 'guests',
        sectionLabel: 'Guests',
        label: 'Deduplication',
        path: '/system/deduplication',
        description: 'Manage profile deduplication tasks and view duplicate detection results.',
        requiredPermissions: [Capability.GET_PROFILES],
    },
    {
        id: 'profileClusters',
        sectionId: 'guests',
        sectionLabel: 'Guests',
        label: 'Profile clusters',
        path: '/system/profileClusters',
        requiredPermissions: [Capability.GET_PROFILES],
    },
    {
        id: 'resources',
        sectionId: 'resourceManagement',
        sectionLabel: 'Resource management',
        label: 'Resources',
        path: '/system/resources',
        description: 'Manage all your resources (rooms, staff, facilities, restaurants, rentals, etc.) in one place.',
        createLabel: 'Create resource',
        createPath: '/system/resources/new',
        keywords: ['rooms', 'staff', 'facilities'],
        requiredPermissions: [Capability.GET_RESOURCES],
    },
    {
        id: 'resourceCategories',
        sectionId: 'resourceManagement',
        sectionLabel: 'Resource management',
        label: 'Resource categories',
        path: '/system/resourceCategories',
        description: 'Create categories to organize resources.',
        createLabel: 'Create resource category',
        createPath: '/system/resourceCategories/new',
        requiredPermissions: [Capability.GET_RESOURCES],
    },
    {
        id: 'services',
        sectionId: 'services',
        sectionLabel: 'Services',
        label: 'Services',
        path: '/system/services',
        description: 'Create and manage all services, such as room rentals, restaurant reservations, spa and beauty treatments, custom services and more.',
        createLabel: 'Create service',
        createPath: '/system/services/new',
        requiredPermissions: [Capability.GET_SERVICES],
    },
    {
        id: 'serviceCategories',
        sectionId: 'services',
        sectionLabel: 'Services',
        label: 'Service categories',
        path: '/system/serviceCategories',
        description: 'Organize services in different service categories',
        createLabel: 'Create service category',
        createPath: '/system/serviceCategories/new',
        requiredPermissions: [Capability.GET_SERVICES],
    },
    {
        id: 'rates',
        sectionId: 'services',
        sectionLabel: 'Services',
        label: 'Rates / Prices',
        path: '/system/rates',
        description: 'The most flexible way to define rates',
        createLabel: 'Create rate',
        createPath: '/system/rates/new',
        keywords: ['prices'],
        requiredPermissions: [Capability.GET_SERVICES],
    },
    {
        id: 'taxRates',
        sectionId: 'services',
        sectionLabel: 'Services',
        label: 'Tax rates',
        path: '/system/taxRates',
        description: 'The most flexible way to define tax rates',
        createLabel: 'Create tax rate',
        createPath: '/system/taxRates/new',
        requiredPermissions: [Capability.GET_SERVICES],
    },
    {
        id: 'ageCategories',
        sectionId: 'services',
        sectionLabel: 'Services',
        label: 'Age categories',
        path: '/system/ageCategories',
        description: 'Define age categories specific to your needs.',
        createLabel: 'Create age category',
        createPath: '/system/ageCategories/new',
        requiredPermissions: [Capability.GET_SERVICES],
    },
    {
        id: 'guestCategories',
        sectionId: 'services',
        sectionLabel: 'Services',
        label: 'Guest categories',
        path: '/system/guestCategories',
        description: 'Define guest categories using completed stays and nights stayed.',
        createLabel: 'Create guest category',
        createPath: '/system/guestCategories/new',
        keywords: ['guests', 'stays', 'nights', 'returning', 'Stammgast'],
        requiredPermissions: [Capability.GET_SERVICES],
    },
    {
        id: 'printTemplates',
        sectionId: 'services',
        sectionLabel: 'Services',
        label: 'Print templates',
        path: '/system/printTemplates',
        description: 'Create and manage print templates for invoices, receipts, and more.',
        createLabel: 'Create print template',
        createPath: '/system/printTemplates/new',
        requiredPermissions: [Capability.GET_SERVICES],
    },
    {
        id: 'printouts',
        sectionId: 'services',
        sectionLabel: 'Services',
        label: 'Printouts (new)',
        path: '/system/printouts',
        description: 'Create and manage printouts such as reservation confirmations and folios.',
        requiredPermissions: [Capability.GET_SERVICES],
    },
    {
        id: 'reservations',
        sectionId: 'reservations',
        sectionLabel: 'Reservations',
        label: 'Reservations',
        path: '/system/reservations',
        description: 'Overview of all service reservations',
        createLabel: 'Create reservation',
        createPath: '/system/reservations/new',
        requiredPermissions: [Capability.GET_RESERVATIONS],
    },
    {
        id: 'messages',
        sectionId: 'communication',
        sectionLabel: 'Communication',
        label: 'Messages',
        path: '/system/correspondenceMessages',
        description: 'View and manage all correspondence messages (emails, SMS, chat)',
        requiredPermissions: [Capability.SEND_EMAIL, Capability.SEND_SMS],
    },
    {
        id: 'folios',
        sectionId: 'billing',
        sectionLabel: 'Billing',
        label: 'Folios',
        path: '/system/folios',
        description: 'Create and folios for your guests',
        requiredPermissions: [Capability.GET_INVOICES, Capability.ROOM_BILLING],
    },
    {
        id: 'digitalSignings',
        sectionId: 'billing',
        sectionLabel: 'Billing',
        label: 'Signatures',
        path: '/system/signatures',
        description: 'Every signing link issued to a guest, with its status, the tip and the signature.',
        keywords: ['tip', 'signature', 'spa', 'Trinkgeld', 'Unterschrift', 'guest confirmation', 'Gästebestätigung'],
        requiredPermissions: [Capability.GET_INVOICES, Capability.ROOM_BILLING],
    },
    {
        id: 'digitalSigningTemplates',
        sectionId: 'billing',
        sectionLabel: 'Billing',
        label: 'Digital signing',
        path: '/system/digitalSigning',
        description: 'The signing pages guests see: where each is offered, its design and how the tip is suggested.',
        keywords: ['tip', 'signature', 'spa', 'restaurant', 'design', 'Trinkgeld', 'Unterschrift', 'guest confirmation', 'template'],
        requiredPermissions: [Capability.GET_INVOICES, Capability.ROOM_BILLING],
    },
    {
        id: 'surveys',
        sectionId: 'surveys',
        sectionLabel: 'Surveys',
        label: 'Surveys',
        path: '/system/surveys',
        description: 'Create surveys, edit their questions and design, and publish them.',
        keywords: ['feedback', 'questionnaire', 'Umfrage', 'Fragebogen'],
        requiredPermissions: [],
        requiredAppSku: AppSku.SURVEYS,
    },
    {
        id: 'surveySubmissions',
        sectionId: 'surveys',
        sectionLabel: 'Surveys',
        label: 'Submissions',
        path: '/system/surveySubmissions',
        description: 'Every response guests sent, across all surveys.',
        keywords: ['responses', 'answers', 'feedback', 'Antworten'],
        requiredPermissions: [],
        requiredAppSku: AppSku.SURVEYS,
    },
    {
        id: 'surveyInvitations',
        sectionId: 'surveys',
        sectionLabel: 'Surveys',
        label: 'Invitations',
        path: '/system/surveyInvitations',
        description: 'Every survey link sent to a guest, with its status.',
        keywords: ['links', 'sent', 'Einladungen'],
        requiredPermissions: [],
        requiredAppSku: AppSku.SURVEYS,
    },
    {
        id: 'team',
        sectionId: 'teamOrganization',
        sectionLabel: 'Team & Organization',
        label: 'Team',
        path: '/system/team',
        description: 'Invite team members and manage access permissions.',
        createLabel: 'Add user',
        createPath: '/system/team/new',
        requiredPermissions: [],
    },
    {
        id: 'logs',
        sectionId: 'teamOrganization',
        sectionLabel: 'Team & Organization',
        label: 'Logs',
        path: '/system/logs',
        requiredPermissions: [],
        requiresCanViewLogs: true,
    },
    {
        id: 'organization',
        sectionId: 'teamOrganization',
        sectionLabel: 'Team & Organization',
        label: 'My organization',
        path: '/system/organization',
        description: 'Manage basic organization info such as name, logo, address, etc.',
        requiredPermissions: [],
    },
    {
        id: 'sectors',
        sectionId: 'teamOrganization',
        sectionLabel: 'Team & Organization',
        label: 'Sectors',
        path: '/system/sectors',
        description: 'Manage sectors',
        createLabel: 'Create sector',
        createPath: '/system/sectors/new',
        requiredPermissions: [],
    },
    {
        id: 'agenda',
        sectionId: 'teamOrganization',
        sectionLabel: 'Team & Organization',
        label: 'Agenda',
        path: '/system/agenda',
        description: 'Manage agendas',
        createLabel: 'Create agenda',
        createPath: '/system/agenda/new',
        requiredPermissions: [Capability.GET_RESERVATIONS],
    },
    {
        id: 'apiKeys',
        sectionId: 'teamOrganization',
        sectionLabel: 'Team & Organization',
        label: 'API keys',
        path: '/system/apiKeys',
        description: 'Manage API keys',
        createLabel: 'Create API key',
        createPath: '/system/apiKeys/new',
        requiredPermissions: [],
    },
    {
        id: 'billingSubscription',
        sectionId: 'teamOrganization',
        sectionLabel: 'Team & Organization',
        label: 'Billing',
        path: '/system/billing',
        description: 'Manage subscription and billing information',
        requiredPermissions: [],
    },
    {
        id: 'contents',
        sectionId: 'contentManagement',
        sectionLabel: 'Content management',
        label: 'Content',
        path: '/system/contents',
        description: 'Manage content such as posts, data and more via generic content wrapper',
        createLabel: 'Create content',
        createPath: '/system/contents/new',
        requiredPermissions: [Capability.GET_CONTENT],
        requiresGuestAppModule: true,
    },
    {
        id: 'contentCategories',
        sectionId: 'contentManagement',
        sectionLabel: 'Content management',
        label: 'Content categories',
        path: '/system/contentCategories',
        description: 'Organize content into categories',
        createLabel: 'Create content category',
        createPath: '/system/contentCategories/new',
        requiredPermissions: [Capability.GET_CONTENT],
        requiresGuestAppModule: true,
    },
    {
        id: 'newsletterAccount',
        sectionId: 'marketing',
        sectionLabel: 'Marketing',
        label: 'Newsletter account',
        path: '/system/newsletterAccount',
        description: 'Configure your newsletter account credentials.',
        requiredPermissions: [Capability.SEND_EMAIL, Capability.SEND_SMS],
    },
    {
        id: 'consentSettings',
        sectionId: 'marketing',
        sectionLabel: 'Marketing',
        label: 'Consent settings',
        path: '/system/audienceGlobalConfig',
        requiredPermissions: [Capability.SEND_EMAIL, Capability.SEND_SMS],
    },
    {
        id: 'transactionalMails',
        sectionId: 'marketing',
        sectionLabel: 'Marketing',
        label: 'Transactional Mails',
        path: '/system/transactionalMailSettings',
        requiredPermissions: [Capability.SEND_EMAIL, Capability.SEND_SMS],
    },
    {
        id: 'marketingStatistics',
        sectionId: 'marketing',
        sectionLabel: 'Marketing',
        label: 'Marketing statistics',
        path: '/system/marketingStatistics',
        requiredPermissions: [Capability.SEND_EMAIL, Capability.SEND_SMS],
    },
    {
        id: 'metaConnector',
        sectionId: 'marketing',
        sectionLabel: 'Marketing',
        label: 'Meta connector',
        path: '/system/metaConnector',
        requiredPermissions: [Capability.SEND_EMAIL, Capability.SEND_SMS],
    },
    {
        id: 'forms',
        sectionId: 'marketing',
        sectionLabel: 'Marketing',
        label: 'Forms',
        path: '/system/forms',
        requiredPermissions: [Capability.SEND_EMAIL, Capability.SEND_SMS],
    },
] as const satisfies readonly SettingsNavigationDefinition[]

export type SettingsNavigationId = typeof SETTINGS_NAVIGATION_DEFINITIONS[number]['id']

const settingsNavigationDefinitions: readonly SettingsNavigationDefinition[] = SETTINGS_NAVIGATION_DEFINITIONS

export const resolveSettingsRoute = (pathname: string): SettingsNavigationDefinition | undefined => {
    const normalizedPathname = normalizePathname(pathname)
    return settingsNavigationDefinitions
        .filter(candidate => isSameOrChildPath(normalizedPathname, candidate.path))
        .sort((left, right) => right.path.length - left.path.length)[0]
}
